import React, { useMemo, useState, useRef, useEffect } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth, useTheme } from '../context/AppContext'
import { pushSupported, getSubscription, enablePush, disablePush } from '../services/push'
import ImpersonationBanner from './ImpersonationBanner'
import logo from '../assets/logo.png'
import {
  LayoutDashboard,
  BarChart3, 
  Users,
  Building2,
  Route,
  CheckCircle2,
  XCircle,
  Send,
  MessageSquare,
  Webhook,
  FileText,
  PhoneOff,
  Megaphone,
  LogOut,
  Menu,
  X,
  Handshake,
  Moon,
  Sun,
  Sparkles,
  History,
  Zap,
  DollarSign,
  ChevronDown,
  Bell,
  Mail,
  Gavel,
  Receipt,
  Settings,
  LayoutTemplate
} from 'lucide-react'

const NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, to: '/' },
  { group: 'Leads' },
  { label: 'Leads Dashboard', icon: BarChart3, to: '/admin-leads', staffOnly: true },
  { label: 'Lead Inspector', icon: History, to: '/lead-inspector', staffOnly: true },
  { label: 'Blocked at intake', icon: XCircle, to: '/rejected-submissions', staffOnly: true },
  { group: 'Management', staffOnly: true },
  { label: 'Companies', icon: Building2, to: '/companies', staffOnly: true },
  { label: 'Affiliates', icon: Handshake, to: '/affiliates', staffOnly: true },
  { label: 'Campaigns', icon: Megaphone, to: '/campaigns', staffOnly: true },
  { label: 'Buyers', icon: Building2, to: '/buyers', staffOnly: true },
  { label: 'Buyer Sets', icon: Route, to: '/buyer-sets', staffOnly: true },
  { label: 'Flow Templates', icon: LayoutTemplate, to: '/flow-templates', staffOnly: true },
  { group: 'Financial', staffOnly: true },
  // Rutas sin prefijo común a propósito. `currentTitle`/`currentSection` casan
  // con `location.pathname.startsWith(item.to)`, así que un par /financial-*
  // colisionaría con el /financial que ahora sólo redirige, y el primero del
  // array se quedaría el título de los dos.
  { label: 'Accounts receivable', icon: DollarSign, to: '/receivables', adminOnly: true, staffOnly: true },
  { label: 'Accounts payable', icon: DollarSign, to: '/payables', adminOnly: true, staffOnly: true },
  // /company-finances y no /financial-company por lo mismo de arriba: el
  // /financial que redirige a /receivables se lo tragaría con `startsWith`.
  // Tampoco colisiona con /companies — 'company-finances' diverge en la 8ª
  // letra, así que Companies no le roba el título ni al revés.
  { label: 'Company finances', icon: Receipt, to: '/company-finances', adminOnly: true, staffOnly: true },
  { group: 'Analytics', staffOnly: true },
  { label: 'Waterfall Validator', icon: Zap, to: '/waterfall-validator', staffOnly: true },
  { label: 'Auction Validator', icon: Gavel, to: '/auction-validator', staffOnly: true },
  { label: 'Email Remarketing', icon: Mail, to: '/email-reengagement', staffOnly: true },
  { group: 'Compliance', staffOnly: true },
  { label: 'Email Opt Outs', icon: Mail, to: '/sms-suppression', staffOnly: true },
  { label: 'TCPA', icon: FileText, to: '/tcpa', staffOnly: true },
  { group: 'Administration' },
  { label: 'Users', icon: Users, to: '/users', adminOnly: true },
  { label: 'Account', icon: Settings, to: '/account', staffOnly: true },
]

// Sidebar exclusivo para usuarios partner. Solo ven su propio panel,
// nada más. El backend además les niega cualquier endpoint que no sea
// /core/partner/*, así que aunque navegaran manualmente a otra URL no
// podrían cargar datos.
const NAV_PARTNER = [
  { label: 'Campaigns', icon: Megaphone, to: '/partner-dashboard' },
]

export default function Layout() {
  const { user, logout, isAdmin, isPartner, impersonation } = useAuth()
  const { toggle, isDark } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [sideOpen, setSideOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const [pushReady, setPushReady] = useState(false)
  const [pushOn, setPushOn] = useState(false)

  // Covers both the explicit "End session" click and a silent transition
  // (TTL expiry, or another tab ending the session - AppContext reconciles
  // both into `impersonation` going inactive). Without this, a superuser
  // stranded on /partner-dashboard after their scope override disappears
  // sees API calls start 403ing with no obvious way out - the banner
  // clearing isn't enough of a signal on its own.
  const wasImpersonatingRef = useRef(false)
  useEffect(() => {
    const isActive = !!impersonation?.active
    if (wasImpersonatingRef.current && !isActive && location.pathname.startsWith('/partner-dashboard')) {
      navigate('/companies')
    }
    wasImpersonatingRef.current = isActive
  }, [impersonation?.active, location.pathname, navigate])

   // Elegimos qué NAV usar según el rol. Los partners ven un menú
  // reducido a su propio panel; el resto ve el menú completo de admin.
  const navItems = isPartner ? NAV_PARTNER : NAV
  const roleLabel = isAdmin ? 'Admin' : isPartner ? 'Partner' : 'User'

  const currentTitle = useMemo(() => {
    const item = navItems.find((n) => n.to && n.to !== '/' && location.pathname.startsWith(n.to))
    if (item) return item.label
    if (location.pathname === '/') return 'Business Dashboard'
    return 'Workspace'
  }, [location.pathname])

  const currentSection = useMemo(() => {
    const lastGroupIndex = [...navItems]
      .reverse()
      .findIndex((item) => item.group || (item.to && item.to !== '/' && location.pathname.startsWith(item.to)))

    if (location.pathname === '/') return 'Overview'
    const index = navItems.length - 1 - lastGroupIndex
    for (let i = index; i >= 0; i -= 1) {
      if (navItems[i]?.group) return navItems[i].group
    }
    return 'Workspace'
  }, [location.pathname])

  const displayName = useMemo(() => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
    return fullName || user?.email || 'User'
  }, [user])

  const initials = useMemo(() => {
    const fromName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
    if (fromName) return fromName
    return user?.email?.slice(0, 2)?.toUpperCase() || 'KU'
  }, [user])

  const handleLogout = () => {
    setUserMenuOpen(false)
    logout()
    navigate('/login')
  }

  // Detecta si el dispositivo soporta push y si ya hay una suscripción activa.
  useEffect(() => {
    if (!pushSupported()) return
    getSubscription()
      .then((sub) => {
        setPushOn(!!sub)
        setPushReady(true)
      })
      .catch(() => setPushReady(true))
  }, [])

  const handleEnableNotifications = async () => {
    setUserMenuOpen(false)
    try {
      await enablePush()
      setPushOn(true)
      toast.success('Notifications enabled.')
    } catch (e) {
      if (e.message === 'ios-not-installed') {
        toast('On iPhone, add the app to your Home Screen first, then enable notifications.', { duration: 6000 })
      } else if (e.message === 'denied') {
        toast.error('Notifications are blocked. Enable them in your browser settings.')
      } else if (e.message === 'unsupported') {
        toast.error('This device does not support notifications.')
      } else {
        toast.error('Could not enable notifications.')
      }
    }
  }

  const handleDisableNotifications = async () => {
    setUserMenuOpen(false)
    try {
      await disablePush()
      setPushOn(false)
      toast('Notifications disabled.')
    } catch (e) {
      toast.error('Could not disable notifications.')
    }
  }

  // Cierra el menú de usuario al hacer clic afuera.
  useEffect(() => {
    if (!userMenuOpen) return
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [userMenuOpen])

  const SidebarContent = () => (
    <>
      <div className="sidebar-brand">
        <div className="sidebar-brand-mark">
          <img
            src={logo}
            alt="Kickoff"
            style={{
              height: 34,
              width: 'auto',
              objectFit: 'contain',
              filter: isDark ? 'brightness(0) invert(1)' : 'none',
              transition: 'filter 0.3s ease',
            }}
          />
        </div>
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item, i) => {
          if (item.staffOnly && !user?.is_staff) return null
          if (item.group) {
            return (
              <div key={`group-${item.group}-${i}`} className="nav-group-label">
                {item.group}
              </div>
            )
          }
          if (item.adminOnly && !isAdmin) return null
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `nav-item ${item.child ? 'nav-item-child' : ''} ${isActive ? 'active' : ''}`}
              onClick={() => setSideOpen(false)}
              style={item.child ? { paddingLeft: 34, position: 'relative' } : undefined}
            >
              {item.child ? (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 20,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: 'var(--border-strong)',
                    opacity: 0.5,
                  }}
                />
              ) : null}
              <span className="nav-item-icon">
                <item.icon size={item.child ? 13 : 15} />
              </span>
              <span>{item.label}</span>
            </NavLink>
          )
        })}
      </nav>
    </>
  )

  return (
    <div className="app-layout">
      {sideOpen ? <div className="mobile-scrim" onClick={() => setSideOpen(false)} /> : null}

      <aside className="sidebar sidebar-desktop">
        <SidebarContent />
      </aside>

      {sideOpen ? (
        <aside className="sidebar sidebar-mobile">
          <SidebarContent />
        </aside>
      ) : null}

      <main className="main-area">
        <header className="topbar topbar-elevated">
          <div className="topbar-start">
            <button className="btn btn-ghost btn-icon layout-menu-btn" onClick={() => setSideOpen(!sideOpen)}>
              {sideOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
            <div>
              <div className="topbar-kicker">{currentSection}</div>
              <div className="breadcrumb">{currentTitle}</div>
            </div>
          </div>

          <div className="topbar-actions">
            <button
              className="theme-toggle theme-toggle-elevated"
              onClick={toggle}
              title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              <span className="theme-toggle-icon theme-toggle-icon-sun">
                <Sun size={12} />
              </span>
              <span className="theme-toggle-icon theme-toggle-icon-moon">
                <Moon size={12} />
              </span>
            </button>

            <div className="topbar-user" ref={userMenuRef}>
              <button
                type="button"
                className="topbar-user-chip"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <div className="topbar-user-avatar">{initials}</div>
                <div className="topbar-user-copy">
                  <strong>{displayName}</strong>
                  <span>{roleLabel}</span>
                </div>
                <ChevronDown size={14} className="topbar-user-caret" />
              </button>

              {userMenuOpen ? (
                <div className="user-menu" role="menu">
                  <div className="user-menu-head">
                    <strong>{displayName}</strong>
                    <span>{roleLabel}</span>
                  </div>
                  {pushReady ? (
                    <button
                      type="button"
                      className="user-menu-item"
                      role="menuitem"
                      onClick={pushOn ? handleDisableNotifications : handleEnableNotifications}
                    >
                      <Bell size={14} />
                      {pushOn ? 'Disable notifications' : 'Enable notifications'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="user-menu-item"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <ImpersonationBanner />

        <div className="page-content fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
